import type { Pagination as PaginationType } from '@grafikr/shopify-typescript/type/liquid/Pagination';
import { App, Component } from '@grafikr/components';
import { clamp } from 'lodash-es';

import { NonNullish } from 'utils/nullish-check';
import setUrlParams from 'utils/set-url-params';

type Part = {
  page: number;
  size: number;
  url: URL;
  isLoaded: boolean;
};

const tabbableSelectors = [
  'input:not([inert])',
  'select:not([inert])',
  'textarea:not([inert])',
  'a[href]:not([inert])',
  'button:not([inert])',
  '[tabindex]:not(slot):not([inert])',
  'audio[controls]:not([inert])',
  'video[controls]:not([inert])',
  '[contenteditable]:not([contenteditable="false"]):not([inert])',
  'details>summary:first-of-type:not([inert])',
  'details:not([inert])',
];

class Pagination {
  private container: HTMLElement;
  private data!: PaginationType;
  private grid!: HTMLElement;
  private count!: HTMLElement;
  private progress!: HTMLElement;
  private previousButton: HTMLButtonElement | null;
  private nextButton: HTMLButtonElement | null;
  private app: App;

  private parts: Map<number, Part> = new Map();
  private previousPart: Part | null = null;
  private nextPart: Part | null = null;

  constructor(node: HTMLElement, app: App) {
    this.container = node;
    this.app = app;
    this.previousButton = this.container.querySelector<HTMLButtonElement>('[data-previous]');
    this.nextButton = this.container.querySelector<HTMLButtonElement>('[data-next]');

    this.init();
  }

  init() {
    this.data = JSON.parse(NonNullish(this.container.querySelector('[data-data]')?.textContent));
    this.grid = NonNullish(document.querySelector(NonNullish(this.container.dataset.gridSelector)));

    this.count = NonNullish(this.container.querySelector('[data-count]'));

    this.progress = NonNullish(this.container.querySelector<HTMLElement>('[data-progress]'));

    this.previousButton?.addEventListener('click', this.loadPrevious.bind(this));
    this.nextButton?.addEventListener('click', this.loadNext.bind(this));

    this.updateParts();
    this.updateProgress();
  }

  updateParts() {
    for (let i = 0; i < this.data.pages; i += 1) {
      const page = i + 1;
      const size =
        (i === this.data.pages - 1 ? this.data.items % this.data.page_size : this.data.page_size) ||
        this.data.page_size;
      const url = new URL(window.location.toString());
      url.searchParams.append('page', (i + 1).toString());
      url.searchParams.append('section_id', NonNullish(this.container.dataset.sectionId));

      this.parts.set(page, {
        page,
        size,
        url,
        isLoaded: page === this.data.current_page,
      });
    }

    const prevPart = this.parts.get(this.data.current_page - 1);
    const nextPart = this.parts.get(this.data.current_page + 1);

    this.previousPart = prevPart || null;
    this.nextPart = nextPart || null;
  }

  updatePageParam(part: Part) {
    const params = setUrlParams(new URLSearchParams(window.location.search), {
      page: part.page.toString(),
    });

    window.history.replaceState(null, '', `?${params.toString()}`);
  }

  updateProgress() {
    let currentOffset = 0;
    let currentProgress = 0;
    let hasFoundLoadedPart = false;

    this.parts.forEach((part) => {
      if (part.isLoaded) {
        currentProgress += part.size;
        hasFoundLoadedPart = true;
      }

      if (!hasFoundLoadedPart) {
        currentOffset += part.size;
      }
    });

    currentProgress = clamp(currentProgress, 0, this.data.items);

    this.count.textContent = currentProgress.toString();
    this.progress.style.setProperty('--progress', `${(currentProgress / this.data.items) * 100}%`);
    this.progress.style.setProperty('--offset', `${(currentOffset / this.data.items) * 100}%`);
  }

  async loadPart(part: Part) {
    if (part.isLoaded) {
      throw new Error('Pagination part is already loaded or loading');
    }

    part.isLoaded = true;
    this.updateProgress();

    const response = await fetch(part.url);
    const text = await response.text();

    const html = new DOMParser().parseFromString(text, 'text/html');

    return NonNullish(html.querySelector(NonNullish(this.container.dataset.gridSelector)))
      .innerHTML;
  }

  insertItems(position: 'previous' | 'next', text: string, autoFocus: boolean) {
    this.grid.role = 'alert';

    const content = document.createElement('div');
    content.insertAdjacentHTML(position === 'previous' ? 'afterbegin' : 'beforeend', text);
    const children = Array.from(content.children);

    if (position === 'previous') {
      this.grid.prepend(...children);

      if (autoFocus) {
        children.at(-1)?.querySelector<HTMLElement>(tabbableSelectors.join(', '))?.focus();
      }
    } else {
      this.grid.append(...children);

      if (autoFocus) {
        children.at(0)?.querySelector<HTMLElement>(tabbableSelectors.join(', '))?.focus();
      }
    }

    this.app.mount();
  }

  async loadPrevious(event?: Event) {
    if (!this.previousPart) {
      return;
    }

    if (event) {
      this.updatePageParam(this.previousPart);
    }

    this.insertItems('previous', await this.loadPart(this.previousPart), event !== undefined);

    const newPreviousPart = this.parts.get(this.previousPart.page - 1);
    this.previousPart = newPreviousPart || null;

    if (!this.previousPart && this.previousButton) {
      this.previousButton.remove();
    }
  }

  async loadNext(event?: Event) {
    if (!this.nextPart) {
      return;
    }

    if (event) {
      this.updatePageParam(this.nextPart);
    }

    this.insertItems('next', await this.loadPart(this.nextPart), event !== undefined);

    const newNextPart = this.parts.get(this.nextPart.page + 1);
    this.nextPart = newNextPart || null;

    if (!this.nextPart && this.nextButton) {
      this.nextButton.remove();
    }
  }
}

export default Component(async (node, { app }) => {
  const pagination = new Pagination(node, app);

  document.addEventListener('filter:update', () => {
    pagination.init();
  });
});
